/* ============================================
   FOOTER COMPONENT
   The Creative Orchestra - GEMINI Framework
   Site footer with links and credits
   ============================================ */

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Linkedin, 
  Globe, 
  Mail, 
  Heart,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { SITE_INFO, AUTHOR_INFO, WORKS_ORDER } from '@utils/constants';
import { works } from '@data';

/**
 * Footer Component
 * Responsive footer with:
 * - Site branding
 * - Navigation links
 * - Work links grouped by stage
 * - Social links
 * - Credits and copyright
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Group works by stage for footer
  const workStages = [
    { name: 'Generation', works: ['genie', 'midas'] },
    { name: 'Evaluation', works: ['magics'] },
    { name: 'Management', works: ['dimes', 'aegis'] },
    { name: 'INspiration', works: ['euphoria', 'retina', 'fenso'] },
  ];

  return (
    <footer className="relative mt-auto border-t border-white/10 bg-black/40 backdrop-blur-sm">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4 group">
              <motion.div
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30 
                  flex items-center justify-center border border-white/10"
              >
                <Sparkles className="w-5 h-5 text-white" />
              </motion.div>
              <div>
                <span className="text-lg font-display font-semibold text-white">
                  GEMINI
                </span>
                <span className="text-xs text-gray-400 block">
                  The Creative Orchestra
                </span>
              </div>
            </Link>
            
            <p className="text-sm text-gray-400 mb-6 max-w-xs">
              A Designer's Quest to Humanize Artificial Intelligence. 
              Where human dreams meet machine intelligence.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              <SocialLink 
                href={AUTHOR_INFO.linkedin} 
                icon={<Linkedin className="w-4 h-4" />}
                label="LinkedIn"
              />
              <SocialLink 
                href={AUTHOR_INFO.website} 
                icon={<Globe className="w-4 h-4" />}
                label="Website"
              />
              <SocialLink 
                href={`mailto:${AUTHOR_INFO.email}`} 
                icon={<Mail className="w-4 h-4" />}
                label="Email"
              />
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
              Navigation
            </h4>
            <ul className="space-y-2">
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/novel">Read the Novel</FooterLink>
              <FooterLink to="/about">About</FooterLink>
              <FooterLink to="/publications">Publications</FooterLink>
              <FooterLink to="/acknowledgements">Acknowledgements</FooterLink>
            </ul>
          </div>

          {/* Works Column */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
              Works
            </h4>
            <ul className="space-y-2">
              {WORKS_ORDER.map((workId) => {
                const work = works[workId];
                return (
                  <FooterLink key={workId} to={`/work/${workId}`}>
                    {work?.name || workId.toUpperCase()}
                  </FooterLink>
                );
              })}
            </ul>
          </div>

          {/* Research Column */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
              Research
            </h4>
            <ul className="space-y-2">
              <FooterLink to="/novel/01">Prologue</FooterLink>
              <FooterLink to="/novel/27">GEMINI Framework</FooterLink>
              <FooterLink to="/novel/32">Epilogue</FooterLink>
              <li>
                <a 
                  href={AUTHOR_INFO.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-400 hover:text-white transition-colors 
                    inline-flex items-center gap-1"
                >
                  IISc Bangalore
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col items-center gap-4">
            {/* Made with love */}
            <p className="text-sm text-gray-400 flex items-center gap-2 flex-wrap justify-center">
              Made with 
              <Heart className="w-4 h-4 text-red-400 fill-red-400" />
              by {AUTHOR_INFO.name} •
              <a 
                href={AUTHOR_INFO.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center gap-1"
              >
                LinkedIn <ExternalLink className="w-3 h-3" />
              </a>
              •
              <a 
                href={AUTHOR_INFO.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center gap-1"
              >
                {AUTHOR_INFO.website.replace('https://', '')} <ExternalLink className="w-3 h-3" />
              </a>
            </p>

            {/* Copyright */}
            <p className="text-sm text-gray-500 text-center">
              © {SITE_INFO.year} All Rights Reserved • The GEMINI Project • PhD, IISc
            </p>
          </div>
        </div>
      </div>

      {/* Decorative gradient */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(129, 140, 248, 0.3), transparent)',
        }}
      />
    </footer>
  );
};

/**
 * Footer Link Component
 */
const FooterLink = ({ to, children }) => {
  return (
    <li>
      <Link 
        to={to}
        className="text-sm text-gray-400 hover:text-white transition-colors"
      >
        {children}
      </Link>
    </li>
  );
};

/**
 * Social Link Component
 */
const SocialLink = ({ href, icon, label }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      className="w-9 h-9 rounded-full bg-white/5 border border-white/10
        flex items-center justify-center text-gray-400 hover:text-white
        hover:bg-white/10 hover:border-white/20 transition-colors"
      aria-label={label}
    >
      {icon}
    </motion.a>
  );
};

export default Footer;
